/**
 * EshopConnectionsCard — V140.5 active sync UI.
 *
 * Lets the workspace owner save WooCommerce / Shopify credentials so
 * the Product Importer can push parsed feeds directly into their shop.
 * Two-state widget:
 *   - "Disconnected" — empty card with a "+ Pripoj e-shop" CTA opening
 *     the credentials modal.
 *   - "Connected"    — per-platform rows with masked creds, status
 *     badge (ok / error), last-test timestamp, and Test/Remove buttons.
 *
 * Credentials never echo back unmasked. The Save form is the ONLY way
 * to set them — recovery = remove + re-add. (Same pattern as Stripe /
 * Gmail OAuth: secrets are write-only from the UI's perspective.)
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { ShoppingBag, Plug, CheckCircle2, AlertTriangle, X, Trash2, RefreshCw } from "lucide-react";

const PLATFORMS = [
  { id: "woocommerce", label: "WooCommerce", color: "#7F54B3", auth: "basic" },
  { id: "shopify",     label: "Shopify",     color: "#95BF47", auth: "token" },
  { id: "prestashop",  label: "PrestaShop",  color: "#DF0067", auth: "token" },
  { id: "magento",     label: "Magento",     color: "#EE672F", auth: "token" },
  { id: "opencart",    label: "OpenCart",    color: "#1E8AC1", auth: "token" },
];

const TOKEN_HELP = {
  shopify: "Shopify Admin → Apps → Develop apps → API credentials → Admin API access token (scopes: write_products + read_products).",
  prestashop: "PrestaShop Admin → Advanced Parameters → Webservice → Add new webservice key. Povoľ aspoň: products (GET, POST, PUT).",
  magento: "Magento Admin → System → Extensions → Integrations → Add New Integration → Activate. Copy 'Access Token' (Bearer).",
  opencart: "OpenCart Admin → System → Users → API. Po vytvorení Restadmin extension povoľ Cart API + Products. Token = X-Oc-Restadmin-Id.",
};

export default function EshopConnectionsCard({ workspaceId, accent = "#FFB800" }) {
  const [rows, setRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const reload = async () => {
    try {
      setRefreshing(true);
      const r = await axios.get(`${API}/eshop-sync/connections/${workspaceId}`);
      setRows(r.data?.connections || []);
    } catch (e) { /* silent */ }
    setRefreshing(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [workspaceId]);

  const testOne = async (platform) => {
    try {
      await axios.post(`${API}/eshop-sync/test/${workspaceId}/${platform}`);
      reload();
    } catch (e) { /* surface via reload */ }
  };

  const removeOne = async (platform) => {
    if (!window.confirm(`Odpojiť ${platform} z workspace-u?`)) return;
    try {
      await axios.delete(`${API}/eshop-sync/connection/${workspaceId}/${platform}`);
      reload();
    } catch (e) { /* surface via reload */ }
  };

  return (
    <section
      data-testid="eshop-connections-card"
      className="mb-6 border border-gray-800 bg-neutral-950 p-4 sm:p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-black mb-1" style={{ color: accent }}>
            <ShoppingBag className="w-3 h-3 inline mr-1.5" /> E-shop integrácie
          </p>
          <p className="text-sm text-gray-200">
            Pripoj <span className="font-bold">WooCommerce</span> alebo <span className="font-bold">Shopify</span> a Product Importer ti tlačí produkty priamo do shopu.
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-testid="eshop-add-connection-btn"
          className="text-[11px] font-black uppercase tracking-widest px-3 py-1.5 bg-amber-500 text-black"
        >
          + Pripoj
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-gray-500 text-center py-4" data-testid="eshop-empty">
          Žiadne pripojené e-shopy. Klikni „+ Pripoj" vyššie.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="eshop-connections-list">
          {rows.map((c) => (
            <li key={c.platform}
                className="flex items-center justify-between gap-3 border border-gray-900 p-3"
                data-testid={`eshop-conn-${c.platform}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="font-black text-sm text-white">{PLATFORMS.find((x) => x.id === c.platform)?.label || c.platform}</span>
                <span className="text-[11px] text-gray-500 truncate">{c.base_url}</span>
                {c.last_test_status === "ok" ? (
                  <span className="text-[10px] text-emerald-400 font-bold inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> OK
                  </span>
                ) : (
                  <span className="text-[10px] text-red-400 font-bold inline-flex items-center gap-1" title={c.last_test_error || ""}>
                    <AlertTriangle className="w-3 h-3" /> {c.last_test_status || "neotestovaný"}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => testOne(c.platform)}
                  data-testid={`eshop-test-${c.platform}`}
                  className="text-[10px] text-gray-300 hover:text-white border border-gray-800 px-2 py-1"
                  title="Otestuj pripojenie"
                >
                  <RefreshCw className="w-3 h-3" />
                </button>
                <button
                  onClick={() => removeOne(c.platform)}
                  data-testid={`eshop-remove-${c.platform}`}
                  className="text-[10px] text-red-400 hover:text-red-300 border border-red-900 px-2 py-1"
                  title="Odpoj"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showModal && (
        <ConnectionModal
          workspaceId={workspaceId}
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); reload(); }}
        />
      )}
    </section>
  );
}

function ConnectionModal({ workspaceId, onClose, onSaved }) {
  const [platform, setPlatform] = useState("woocommerce");
  const [form, setForm] = useState({
    base_url: "", consumer_key: "", consumer_secret: "", access_token: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setResult(null);
    try {
      const payload = {
        workspace_id: workspaceId,
        platform,
        base_url: form.base_url,
        ...(platform === "woocommerce"
          ? { consumer_key: form.consumer_key, consumer_secret: form.consumer_secret }
          : { access_token: form.access_token }),
      };
      const r = await axios.post(`${API}/eshop-sync/connection`, payload);
      setResult(r.data);
      // Auto-close after 2s if test passed
      if (r.data?.test?.ok) setTimeout(onSaved, 1500);
    } catch (err) {
      setError(err?.response?.data?.detail || "save_failed");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="eshop-connection-modal">
      <div className="bg-neutral-950 border border-gray-800 w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-black text-xl text-white inline-flex items-center gap-2">
            <Plug className="w-5 h-5 text-amber-400" /> Pripoj e-shop
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white" data-testid="eshop-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2 mb-4">
          {PLATFORMS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setPlatform(p.id)}
              data-testid={`eshop-platform-${p.id}`}
              className={`px-2 py-2 text-[11px] font-bold border ${
                platform === p.id ? "border-amber-500 bg-amber-500/10 text-amber-300" : "border-gray-800 text-gray-400 hover:border-gray-700"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Base URL</label>
            <input
              type="url" required
              data-testid="eshop-base-url"
              value={form.base_url}
              onChange={(e) => setForm({ ...form, base_url: e.target.value })}
              placeholder={platform === "woocommerce" ? "https://my-shop.com" :
                           platform === "shopify" ? "https://my-shop.myshopify.com" :
                           platform === "prestashop" ? "https://my-shop.com" :
                           platform === "magento" ? "https://my-shop.com" :
                           "https://my-shop.com"}
              className="w-full bg-black border border-gray-800 text-gray-100 px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
            />
          </div>
          {platform === "woocommerce" ? (
            <>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Consumer key</label>
                <input
                  type="text" required
                  data-testid="eshop-consumer-key"
                  value={form.consumer_key}
                  onChange={(e) => setForm({ ...form, consumer_key: e.target.value })}
                  placeholder="ck_..."
                  className="w-full bg-black border border-gray-800 text-gray-100 px-3 py-2 text-sm font-mono focus:outline-none focus:border-amber-500"
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Consumer secret</label>
                <input
                  type="password" required
                  data-testid="eshop-consumer-secret"
                  value={form.consumer_secret}
                  onChange={(e) => setForm({ ...form, consumer_secret: e.target.value })}
                  placeholder="cs_..."
                  className="w-full bg-black border border-gray-800 text-gray-100 px-3 py-2 text-sm font-mono focus:outline-none focus:border-amber-500"
                />
              </div>
              <p className="text-[11px] text-gray-500">
                WP Admin → WooCommerce → Settings → Advanced → REST API → <strong>Add key</strong> (Read/Write).
              </p>
            </>
          ) : (
            <>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">
                  {platform === "magento" ? "Admin integration access token" :
                   platform === "prestashop" ? "Webservice API key" :
                   platform === "opencart" ? "OpenCart REST token" :
                   "Admin API access token"}
                </label>
                <input
                  type="password" required
                  data-testid="eshop-access-token"
                  value={form.access_token}
                  onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                  placeholder={platform === "shopify" ? "shpat_..." :
                               platform === "magento" ? "atb1k..." :
                               platform === "prestashop" ? "WS_API_KEY..." :
                               "API token"}
                  className="w-full bg-black border border-gray-800 text-gray-100 px-3 py-2 text-sm font-mono focus:outline-none focus:border-amber-500"
                />
              </div>
              <p className="text-[11px] text-gray-500">
                {TOKEN_HELP[platform] || "Skopíruj access token z admin panelu."}
              </p>
            </>
          )}

          {error && <p className="text-xs text-red-400" data-testid="eshop-modal-error">⚠️ {String(error).slice(0, 200)}</p>}
          {result && (
            <p className={`text-xs ${result.test?.ok ? "text-emerald-400" : "text-amber-400"}`} data-testid="eshop-modal-test-result">
              {result.test?.ok ? "✓ Pripojené, test úspešný." : `Uložené, ale test zlyhal: ${result.test?.error?.slice(0, 100) || "unknown"}`}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="eshop-modal-save"
            className="w-full bg-amber-500 text-black font-black py-2.5 text-[12px] uppercase tracking-widest disabled:opacity-50"
          >
            {busy ? "Ukladám…" : "Uložiť a otestovať"}
          </button>
        </form>
      </div>
    </div>
  );
}
